import fallbackImage from '@/assets/images/default.jpg';
import { cn } from '@/utils/cn';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ImageProps {
  src: string | null;
  defaultImage?: string;
  className?: string;
  alt?: string;
  refreshKey?: string | number;
  isZoomable?: boolean;
}

export const Image = ({
  src,
  defaultImage,
  alt = 'image',
  className,
  refreshKey,
  isZoomable = false,
}: ImageProps) => {
  const [currentSrc, setCurrentSrc] = useState<string | null>(src);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    if (src) {
      if (src.startsWith('blob:')) {
        if (src !== currentSrc) {
          setCurrentSrc(src);
        }
      } else if (refreshKey) {
        const cacheBustedSrc = `${src}?v=${refreshKey}`;
        if (cacheBustedSrc !== currentSrc) {
          setCurrentSrc(cacheBustedSrc);
        }
      } else {
        if (src !== currentSrc) {
          setCurrentSrc(src);
        }
      }
    } else {
      if (currentSrc !== null) {
        setCurrentSrc(null);
      }
    }
  }, [src, refreshKey, currentSrc]);

  const fallback = defaultImage ?? fallbackImage;

  const handleImageError = () => {
    if (currentSrc !== fallback) {
      setCurrentSrc(fallback);
    }
  };

  const imgSrc = currentSrc ?? fallback;

  const handleImageClick = () => {
    if (isZoomable) {
      setIsZoomed(true);
      document.body.style.overflow = 'hidden';
    }
  };

  const handleCloseZoom = () => {
    setIsZoomed(false);
    document.body.style.overflow = '';
  };

  return (
    <>
      <img
        key={`${src}-${refreshKey}`}
        src={imgSrc}
        alt={alt}
        className={cn(className, isZoomable && 'cursor-pointer')}
        onError={handleImageError}
        onClick={handleImageClick}
        crossOrigin="use-credentials"
      />

      {isZoomable &&
        isZoomed &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-md"
            onClick={handleCloseZoom}
            role="dialog"
            aria-modal="true"
            aria-label={`${alt} - Vue agrandie`}
          >
            <div className="relative size-full">
              <button
                className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full border border-white/50 bg-black/60 text-white transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/50"
                onClick={handleCloseZoom}
                aria-label="Fermer la vue agrandie"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className="flex size-full items-center justify-center p-4">
                <img
                  src={imgSrc}
                  alt={alt}
                  className="max-h-full max-w-full object-contain"
                  onClick={e => e.stopPropagation()}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
